import pagesData from '../pages.json';

/** Property data uses the literal strings "false" / "null" for empty values. */
export function hasValue(value: unknown): value is string {
  if (value == null) return false;
  const s = String(value).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

export function clean(value: unknown): string {
  return hasValue(value) ? String(value).trim() : '';
}

export function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function formatPhone(raw: string | null | undefined): string {
  if (!hasValue(raw)) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export function telHref(raw: string | null | undefined): string {
  if (!hasValue(raw)) return '';
  return `tel:${raw.replace(/[^+\d]/g, '')}`;
}

export function isExternalUrl(url: string | null | undefined): boolean {
  return !!url && /^https?:\/\//i.test(url);
}

/** Opens off-site links in a new tab unless the editor set an explicit target. */
export function linkTarget(url: string | null | undefined, target: string | null | undefined): string {
  if (hasValue(target) && target !== '_self') return target;
  return isExternalUrl(url) ? '_blank' : '_self';
}

// ============================================================================
// Office hours
// ============================================================================

export interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface GroupedHours {
  label: string;
  hours: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/\s*([ap])\.?m\.?/i, (_m, p: string) => ` ${p.toLowerCase()}m`);
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${match[2]} ${period}`;
}

/** Collapses consecutive days with identical hours into ranges ("Mon - Fri"). */
export function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const key = (d: string | null | undefined) => (d || '').trim().toLowerCase().slice(0, 3);
  const sorted = raw
    .filter((h) => hasValue(h.day))
    .sort((a, b) => {
      const ai = DAY_ORDER.indexOf(key(a.day));
      const bi = DAY_ORDER.indexOf(key(b.day));
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

  const groups: GroupedHours[] = [];
  for (const entry of sorted) {
    const dayLabel = DAY_LABELS[key(entry.day)] || String(entry.day).slice(0, 3);
    const closed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hours = closed
      ? 'Closed'
      : `${formatTime(clean(entry.open_time))} - ${formatTime(clean(entry.close_time))}`;
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hours) {
      const start = prev.label.split(' - ')[0];
      prev.label = `${start} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours });
    }
  }
  return groups;
}

// ============================================================================
// Navigation (built from pages.json)
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  noindex?: boolean;
  template?: string;
}

/** Utility pages reachable from the footer rather than the main nav. */
const NAV_EXCLUDED = new Set(['home', 'privacy-policy', 'accessibility', 'sitemap', 'thank-you']);

export interface NavLink {
  alias: string;
  label: string;
  href: string;
}

/** Visible, non-template pages that opt into the main nav, ordered by position. */
export function buildNavLinks(): NavLink[] {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => !NAV_EXCLUDED.has(alias) && page.visible !== false && !page.template)
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([alias, page]) => ({ alias, label: page.name, href: page.path }));
}

export function isActivePath(current: string | undefined, href: string): boolean {
  if (!current) return false;
  const norm = (p: string) => p.replace(/\/+$/, '').toLowerCase() || '/';
  return norm(current) === norm(href);
}

// ============================================================================
// Shared button styles
// ============================================================================

const btnBase =
  'inline-flex items-center justify-center gap-2 px-7 py-3 min-h-[48px] font-heading text-[17px] font-bold uppercase leading-none tracking-[0.01em] text-center no-underline js-btn focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

/** Filled rust button (the site's primary CTA). */
export const btnPrimary = `${btnBase} border border-primary bg-primary text-[color:var(--vibe-color-text-on-primary)] hover:border-secondary hover:bg-secondary hover:text-[color:var(--vibe-color-text-on-secondary)] focus-visible:outline-primary`;

/** Filled evergreen button (form submit, secondary CTA). */
export const btnSecondary = `${btnBase} border border-secondary bg-secondary text-[color:var(--vibe-color-text-on-secondary)] hover:border-primary hover:bg-primary hover:text-[color:var(--vibe-color-text-on-primary)] focus-visible:outline-secondary`;

/** Outline button for use on rust (primary) surfaces. */
export const btnOutlineOnPrimary = `${btnBase} border border-[color:var(--vibe-color-text-on-primary)] bg-transparent text-[color:var(--vibe-color-text-on-primary)] hover:bg-[color:var(--vibe-color-text-on-primary)] hover:text-primary focus-visible:outline-[color:var(--vibe-color-text-on-primary)]`;

/** Light stone button used beside rust buttons on dark surfaces. */
export const btnStone = `${btnBase} border border-muted bg-muted text-secondary hover:border-primary hover:bg-primary hover:text-[color:var(--vibe-color-text-on-primary)] focus-visible:outline-primary`;
